/**
 * @name useMultiplePrinters
 * @author Keven Adan Bezerra | kevenadanb
 * @description Hook customizado para gerenciar múltiplas impressoras (Totem + Cozinha)
 * @returns {MultiplePrinterHookReturn} Estados e métodos para controle de múltiplas impressoras
 * @package src/hooks/useMultiplePrinters.ts
 */

import { useCallback, useEffect, useState } from "react";
import {
  MultiplePrinterHookReturn,
  PrinterConfiguration,
  QZPrinter,
} from "../types/printer";
import {
  configureQZSecurity,
  eliminateQZTrayDialogs,
  loadJSRSASignLibrary,
  QZ_DEMO_CERT_INFO,
  setupCustomCertificateTrust,
  verifyCertificateConfiguration,
} from "../utils/qzClientSideSigning";
import { useAlert } from "./useAlert";
import { useLoading } from "./useLoading";

// Chaves para localStorage
const STORAGE_KEYS = {
  TOTEM_PRINTER: "qz-totem-printer",
  COZINHA_PRINTER: "qz-cozinha-printer",
  PRINT_MODE: "qz-print-mode", // 'single' ou 'dual'
} as const;

// Declaração global para qz
declare global {
  interface Window {
    qz: any;
  }
}

/**
 * Hook para gerenciar múltiplas impressoras (Totem + Cozinha)
 */
export const useMultiplePrinters = (): MultiplePrinterHookReturn => {
  const { showSuccess, showError, showWarning } = useAlert();
  const { withGlobalLoading, setLoadingState } = useLoading();

  // Estados
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [printers, setPrinters] = useState<QZPrinter[]>([]);
  const [selectedPrinters, setSelectedPrinters] =
    useState<PrinterConfiguration>({
      printerTotem: null,
      printerCozinha: null,
    });
  const [printMode, setPrintMode] = useState<"single" | "dual">(() => {
    return (
      (localStorage.getItem(STORAGE_KEYS.PRINT_MODE) as "single" | "dual") ||
      "dual"
    );
  });

  /**
   * Verifica se QZ Tray está instalado e acessível
   */
  const isQZTrayInstalled = useCallback((): boolean => {
    return typeof window !== "undefined" && window.qz !== undefined;
  }, []);

  /**
   * Retorna URL de download do QZ Tray
   */
  const getQZTrayDownloadUrl = useCallback((): string => {
    return "https://github.com/qzind/tray/releases/download/v2.2.5/qz-tray-2.2.5-x86_64.exe";
  }, []);

  /**
   * Conecta ao QZ Tray com configuração correta para evitar diálogos
   */
  const connectToQZTray = useCallback(async (): Promise<boolean> => {
    if (!isQZTrayInstalled()) {
      showError("QZ Tray não está instalado ou não foi carregado");
      return false;
    }

    // Verificar se já está conectado ou conectando
    if (isConnected || isConnecting) {
      console.log("🔄 QZ Tray já está conectado ou em processo de conexão");
      return isConnected;
    }

    // Verificar se já existe uma conexão WebSocket ativa
    if (window.qz && window.qz.websocket && window.qz.websocket.isActive()) {
      console.log("✅ QZ Tray já possui conexão WebSocket ativa");
      setIsConnected(true);
      return true;
    }

    setIsConnecting(true);

    try {
      console.log("🔐 Iniciando conexão QZ Tray com Client-Side Signing...");

      // 0. PRIMEIRO: Carregar jsrsasign library
      console.log("📋 Passo 0: Carregando jsrsasign library...");
      const jsrsasignLoaded = await loadJSRSASignLibrary();
      if (!jsrsasignLoaded) {
        console.warn("⚠️ jsrsasign não pôde ser carregada, usando fallback");
      }

      // 1. SEGUNDO: Eliminar diálogos antes de qualquer configuração
      console.log("📋 Passo 1: Eliminando diálogos QZ Tray...");
      await eliminateQZTrayDialogs(window.qz);

      // 2. TERCEIRO: Configurar segurança
      console.log("📋 Passo 2: Configurando segurança...");
      await configureQZSecurity(window.qz);

      // 3. QUARTO: Configurar certificados customizados
      console.log("📋 Passo 3: Configurando certificados customizados...");
      try {
        await setupCustomCertificateTrust(window.qz);
        console.log("✅ Certificados customizados configurados");
      } catch (error) {
        console.warn(
          "⚠️ Certificado customizado não pôde ser configurado, usando fallback",
        );
      }

      // 4. QUINTO: Conectar
      console.log("📋 Passo 4: Conectando ao QZ Tray...");
      await window.qz.websocket.connect();

      // 5. SEXTO: Verificar conexão
      if (window.qz.websocket.isActive()) {
        console.log("✅ QZ Tray conectado com sucesso!");

        // 6. SÉTIMO: Verificar configuração de certificados
        console.log("📋 Passo 5: Verificando configuração de certificados...");
        try {
          await verifyCertificateConfiguration();
          console.log("✅ Configuração de certificados verificada!");
        } catch (error) {
          console.warn("⚠️ Configuração de certificados pode estar incompleta");
        }

        setIsConnected(true);
        showSuccess("Conectado ao QZ Tray com sucesso");
        return true;
      } else {
        throw new Error("Falha ao estabelecer conexão");
      }
    } catch (error: any) {
      // Apenas log no console - impressora é opcional
      console.warn(
        "⚠️ QZ Tray não disponível (impressão opcional):",
        error?.message || error,
      );
      console.info(
        'ℹ️ Se aparecer diálogo de certificado do QZ Tray, clique em "Allow".',
      );
      setIsConnected(false);
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [isQZTrayInstalled, showSuccess, showError]);

  /**
   * Busca lista de impressoras disponíveis e define automaticamente padrões se necessário
   */
  const getPrinters = useCallback(async (): Promise<QZPrinter[]> => {
    if (!isConnected) {
      const connected = await connectToQZTray();
      if (!connected) return [];
    }

    try {
      setLoadingState("qz-printers", true);

      const printerList = await window.qz.printers.find();
      const formattedPrinters: QZPrinter[] = printerList.map(
        (printer: string) => ({
          name: printer,
          description: printer,
        }),
      );

      setPrinters(formattedPrinters);

      // 🎯 Auto-seleção inteligente se não há impressoras configuradas
      if (
        formattedPrinters.length > 0 &&
        !selectedPrinters.printerTotem &&
        !selectedPrinters.printerCozinha
      ) {
        let autoSelectedPrinter = null;

        // 1. Buscar impressora padrão do sistema
        try {
          const defaultPrinter = await window.qz.printers.getDefault();
          if (
            defaultPrinter &&
            formattedPrinters.find((p) => p.name === defaultPrinter)
          ) {
            autoSelectedPrinter = defaultPrinter;
            console.log(
              "✅ Impressora padrão do sistema detectada:",
              defaultPrinter,
            );
          }
        } catch (defaultError) {
          console.log(
            "ℹ️ Não foi possível detectar impressora padrão:",
            defaultError,
          );
        }

        // 2. Se não encontrou padrão, buscar "Microsoft Print to PDF"
        if (!autoSelectedPrinter) {
          const pdfPrinter = formattedPrinters.find(
            (p) =>
              p.name.toLowerCase().includes("microsoft print to pdf") ||
              p.name.toLowerCase().includes("print to pdf") ||
              p.name.toLowerCase().includes("pdf"),
          );
          if (pdfPrinter) {
            autoSelectedPrinter = pdfPrinter.name;
            console.log(
              "✅ Microsoft Print to PDF detectado:",
              pdfPrinter.name,
            );
          }
        }

        // 3. Se ainda não encontrou, usar a primeira impressora disponível
        if (!autoSelectedPrinter) {
          autoSelectedPrinter = formattedPrinters[0].name;
          console.log(
            "✅ Primeira impressora disponível selecionada:",
            autoSelectedPrinter,
          );
        }

        // Definir a mesma impressora para ambos inicialmente
        if (autoSelectedPrinter) {
          const newConfig = {
            printerTotem: autoSelectedPrinter,
            printerCozinha: autoSelectedPrinter,
          };
          setSelectedPrinters(newConfig);
          localStorage.setItem(STORAGE_KEYS.TOTEM_PRINTER, autoSelectedPrinter);
          localStorage.setItem(
            STORAGE_KEYS.COZINHA_PRINTER,
            autoSelectedPrinter,
          );
          showSuccess(
            `Impressora "${autoSelectedPrinter}" selecionada para ambas as funções`,
          );
        }
      }

      return formattedPrinters;
    } catch (error) {
      console.error("Erro ao buscar impressoras:", error);
      showError("Erro ao buscar impressoras disponíveis");
      return [];
    } finally {
      setLoadingState("qz-printers", false);
    }
  }, [
    isConnected,
    connectToQZTray,
    selectedPrinters,
    showError,
    showSuccess,
    setLoadingState,
  ]);

  /**
   * Define impressora para via do pedido do totem (alunos)
   */
  const setTotemPrinter = useCallback(
    (printerName: string): void => {
      setSelectedPrinters((prev) => ({ ...prev, printerTotem: printerName }));
      localStorage.setItem(STORAGE_KEYS.TOTEM_PRINTER, printerName);
      showSuccess(`Impressora do Totem: "${printerName}"`);
    },
    [showSuccess],
  );

  /**
   * Define impressora para comandas da cozinha
   */
  const setCozinhaPrinter = useCallback(
    (printerName: string): void => {
      setSelectedPrinters((prev) => ({ ...prev, printerCozinha: printerName }));
      localStorage.setItem(STORAGE_KEYS.COZINHA_PRINTER, printerName);
      showSuccess(`Impressora da Cozinha: "${printerName}"`);
    },
    [showSuccess],
  );

  /**
   * Define a mesma impressora para ambas as funções (totem e cozinha)
   */
  const setBothPrinters = useCallback(
    (printerName: string): void => {
      setSelectedPrinters({
        printerTotem: printerName,
        printerCozinha: printerName,
      });
      localStorage.setItem(STORAGE_KEYS.TOTEM_PRINTER, printerName);
      localStorage.setItem(STORAGE_KEYS.COZINHA_PRINTER, printerName);
      showSuccess(
        `Impressora Única Configurada: "${printerName}" para totem e cozinha`,
      );
    },
    [showSuccess],
  );

  /**
   * Testa impressão em uma impressora específica
   */
  const testPrint = useCallback(
    async (printerName?: string): Promise<boolean> => {
      const printer =
        printerName ||
        selectedPrinters.printerTotem ||
        selectedPrinters.printerCozinha;

      if (!printer) {
        showWarning("Nenhuma impressora selecionada");
        return false;
      }

      if (!isConnected) {
        const connected = await connectToQZTray();
        if (!connected) return false;
      }

      return withGlobalLoading(async () => {
        try {
          const config = window.qz.configs.create(printer);

          const data = [
            "Teste de Impressao - Multiplas Impressoras\n",
            "Sagrada Familia - Sistema de Totem\n",
            "Data/Hora: " + new Date().toLocaleString("pt-BR") + "\n",
            "Impressora: " + printer + "\n",
            "Status: Funcionando corretamente\n",
            "\n",
            "Este e um teste da configuracao de impressoras.\n",
          ];

          await window.qz.print(config, data);
          showSuccess("Teste de impressao enviado com sucesso");
          return true;
        } catch (error) {
          console.error("Erro no teste de impressao:", error);
          showError("Erro ao enviar teste de impressao");
          return false;
        }
      });
    },
    [
      selectedPrinters,
      isConnected,
      connectToQZTray,
      showWarning,
      showSuccess,
      showError,
      withGlobalLoading,
    ],
  );

  /**
   * Retorna informações sobre o certificado QZ Demo Cert configurado
   */
  const getCertificateInfo = useCallback((): typeof QZ_DEMO_CERT_INFO => {
    return QZ_DEMO_CERT_INFO;
  }, []);

  /**
   * Carrega configurações de impressoras salvas no localStorage
   */
  useEffect(() => {
    const savedTotemPrinter = localStorage.getItem(STORAGE_KEYS.TOTEM_PRINTER);
    const savedCozinhaPrinter = localStorage.getItem(
      STORAGE_KEYS.COZINHA_PRINTER,
    );

    if (savedTotemPrinter || savedCozinhaPrinter) {
      setSelectedPrinters({
        printerTotem: savedTotemPrinter,
        printerCozinha: savedCozinhaPrinter,
      });
    }
  }, []);

  /**
   * Auto-conecta ao QZ Tray quando disponível
   */
  useEffect(() => {
    if (isQZTrayInstalled() && !isConnected && !isConnecting) {
      connectToQZTray().catch(console.error);
    }
  }, [isConnected, isConnecting]); // Removido connectToQZTray das dependências

  /**
   * Verifica se está configurado para usar uma única impressora
   */
  const isSinglePrinterMode = useCallback((): boolean => {
    return (
      selectedPrinters.printerTotem === selectedPrinters.printerCozinha &&
      selectedPrinters.printerTotem !== null
    );
  }, [selectedPrinters]);

  /**
   * Alterna entre modo single e dual
   */
  const togglePrintMode = useCallback((): void => {
    const newMode = printMode === "single" ? "dual" : "single";
    setPrintMode(newMode);
    localStorage.setItem(STORAGE_KEYS.PRINT_MODE, newMode);
    showSuccess(
      `Modo de impressão alterado para: ${newMode === "single" ? "Impressora Única" : "Duas Impressoras"}`,
    );
  }, [printMode, showSuccess]);

  /**
   * Define o modo de impressão
   */
  const setPrintModeActive = useCallback(
    (mode: "single" | "dual"): void => {
      setPrintMode(mode);
      localStorage.setItem(STORAGE_KEYS.PRINT_MODE, mode);
      showSuccess(
        `Modo de impressão definido: ${mode === "single" ? "Impressora Única" : "Duas Impressoras"}`,
      );
    },
    [showSuccess],
  );

  /**
   * Verifica se está no modo de impressão ativo
   */
  const isActivePrintMode = useCallback(
    (mode: "single" | "dual"): boolean => {
      return printMode === mode;
    },
    [printMode],
  );

  /**
   * Detecta o modo atual baseado na configuração das impressoras e no modo ativo
   */
  const getCurrentPrintMode = useCallback((): "single" | "dual" => {
    // Se o modo forçado for single, sempre retorna single
    if (printMode === "single") {
      return "single";
    }

    // Se o modo forçado for dual, verifica se tem impressoras diferentes configuradas
    if (printMode === "dual") {
      // Se tem duas impressoras diferentes configuradas, usa dual
      if (
        selectedPrinters.printerTotem !== selectedPrinters.printerCozinha &&
        selectedPrinters.printerTotem &&
        selectedPrinters.printerCozinha
      ) {
        return "dual";
      }
      // Se tem a mesma impressora ou apenas uma configurada, força single
      return "single";
    }

    return "single";
  }, [printMode, selectedPrinters]);

  return {
    isConnected,
    isConnecting,
    printers,
    selectedPrinters,
    connectToQZTray,
    getPrinters,
    setTotemPrinter,
    setCozinhaPrinter,
    setBothPrinters,
    testPrint,
    isQZTrayInstalled,
    getQZTrayDownloadUrl,
    getCertificateInfo,
    isSinglePrinterMode,
    // Novos controles de modo
    printMode,
    togglePrintMode,
    setPrintModeActive,
    isActivePrintMode,
    getCurrentPrintMode,
  };
};
